<template>
  <el-drawer
    :visible.sync="drawerVisible"
    direction="rtl"
    size="90%"
    :show-close="false"
    :wrapper-closable="true"
    :append-to-body="true"
    custom-class="location-drawer"
    @opened="handleOpened"
    @close="handleClose"
  >
    <div class="location-drawer__container">
      <!-- Header -->
      <div class="location-drawer__header">
        <div class="location-drawer__title">
          Find a Think Zone Center near you
        </div>
        <button class="location-drawer__close" @click="handleClose">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="#aeaeae" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="location-drawer__content">
        <!-- Error Overlay -->
        <div v-if="loadFailed || mapLoadFailed" class="location-drawer__error-overlay">
          <div class="location-drawer__error-icon" />
          <h3 class="location-drawer__error-title">
            oops！something went wrong
          </h3>
          <p class="location-drawer__error-desc">
            We're having trouble loading the information.<br>Please try again later.
          </p>
        </div>

        <!-- Left: Location List -->
        <div v-show="!loadFailed && !mapLoadFailed" ref="locationList" class="location-drawer__list">
          <LocationCard
            v-for="(loc, index) in displayLocations"
            :key="loc.venueId || index"
            :ref="`card-${index}`"
            :location="loc"
            :is-active="selectedIndex === index"
            :is-closest="index === 0 && hasGeolocation"
            :mode="mode"
            :enroll-loading="isEnrollLoadingForVenue(loc, 'list')"
            @select="handleCardSelect(index)"
            @deselect="handleCardDeselect(index)"
            @schedule="handleSchedule(loc, 'list')"
            @use-location="handleUseLocation(loc, 'list')"
            @enroll="handleEnroll(loc, 'list')"
            @gotocenterpage="handleGoToCenterPage(loc, 'list')"
          />
        </div>

        <!-- Right: Map -->
        <div v-show="!loadFailed && !mapLoadFailed" class="location-drawer__map">
          <div ref="mapContainer" class="location-drawer__map-container" />
          <!-- Map Popup -->
          <div
            v-if="showPopup && selectedLocation && !mapLoadFailed && !loadFailed"
            ref="mapPopup"
            class="location-drawer__popup"
            :style="popupStyle"
          >
            <div class="location-drawer__popup-image">
              <img v-if="selectedLocation.photo" v-lazy-load :data-src="selectedLocation.photo" :alt="selectedLocation.name">
              <button class="location-drawer__popup-close" aria-label="Close" @click.stop="handlePopupClose">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#FFFFFF" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </div>
            <div class="location-drawer__popup-info">
              <h4 class="location-drawer__popup-name">
                {{ selectedLocation.name }}
              </h4>
              <p class="location-drawer__popup-address">
                {{ selectedLocation.address }}
              </p>
              <div v-if="mode === 'enroll'" class="location-drawer__popup-cta-group">
                <button
                  class="location-drawer__popup-cta location-drawer__popup-cta--use"
                  :class="{ 'is-loading': isEnrollLoadingForVenue(selectedLocation, 'popup') }"
                  :disabled="isEnrollLoadingForVenue(selectedLocation, 'popup')"
                  @click="handleEnroll(selectedLocation, 'popup')"
                >
                  <span v-if="isEnrollLoadingForVenue(selectedLocation, 'popup')" class="location-drawer__btn-spinner" />
                  Enroll Now
                </button>
              </div>
              <div v-else-if="mode === 'select'" class="location-drawer__popup-cta-group">
                <button
                  class="location-drawer__popup-cta location-drawer__popup-cta--use"
                  @click="handleUseLocation(selectedLocation, 'popup')"
                >
                  Use This Location
                </button>
                <a
                  class="location-drawer__popup-cta location-drawer__popup-cta--secondary"
                  :href="popupCenterPageUrl"
                  target="_self"
                  @click="handlePopupCenterPageClick"
                >
                  Go to Center Page
                </a>
              </div>
              <button v-else class="location-drawer__popup-cta" @click="handleSchedule(selectedLocation, 'popup')">
                Schedule an Assessment
              </button>
              <a v-if="selectedLocation.phone" class="location-drawer__popup-phone" :href="isWindows ? undefined : `tel:${selectedLocation.phone}`" @click.prevent="handlePopupPhoneClick">
                <img class="phone-icon" v-lazy-load data-src="~assets/images/bm/icon-tel-red2.webp" alt="">
                <span>{{ selectedLocation.phone }}</span>
              </a>
              <transition name="fade">
                <span v-if="popupCopyTip" class="location-drawer__popup-copy-tip">{{ popupCopyTip }}</span>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import bus from 'common/bus'
import { growthApi } from 'config/api'
import GoogleMapAdapter from 'common/googleMapsAdapter'
import {
  formatLocationDistance,
  trackContactClick,
  trackEvent,
  TRACK_EVENTS,
} from '~/mixins/scheduleEvaluation/tracker'
import LocationCard from './LocationCard.vue'
import {
  DEFAULT_COORDS,
  MAP_DEFAULT_CENTER,
  MAP_DEFAULT_ZOOM,
} from './config'
import { getCenterPageUrl } from 'config/centerPageUrl'

export default {
  name: 'LocationDrawer',
  components: { LocationCard },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // 'schedule'：底部按钮为 Schedule an Evaluation（默认，跳转到 /book-trial 等业务流程）
    // 'select'：底部按钮为 Use This Location + Go to Center Page，用于 header find-location 选址入口
    mode: {
      type: String,
      default: 'schedule',
      validator: (v) => ['schedule', 'select', 'enroll'].includes(v),
    },
  },
  data() {
    return {
      locations: [],
      selectedIndex: 0,
      userLat: null,
      userLng: null,
      hasGeolocation: false,
      adapter: null,
      mapReady: false,
      pendingInit: false,
      showPopup: false,
      popupPosition: { x: 0, y: 0 },
      popupCopyTip: '',
      popupCopyTimer: null,
      loadFailed: false,
      mapLoadFailed: false,
      enrollLoadingVenueId: null,
      enrollLoadingSource: null,
    }
  },
  computed: {
    drawerVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      },
    },
    displayLocations() {
      return this.locations
    },
    selectedLocation() {
      return this.displayLocations[this.selectedIndex] || null
    },
    isWindows() {
      if (typeof navigator === 'undefined') return false
      const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.userAgent
      return /Win/i.test(platform)
    },
    popupStyle() {
      return {
        left: `${this.popupPosition.x}px`,
        top: `${this.popupPosition.y}px`,
        transform: 'translate(-50%, -100%)',
      }
    },
    popupCenterPageUrl() {
      const loc = this.selectedLocation
      if (!loc) return '#'
      return getCenterPageUrl(loc.venueId || loc.id)
    },
    // 地图抽屉 entry_page 取值：
    // - mode=select（Find Location 入口）→ 'location'
    // - mode=enroll（ela enroll 入口）→ 'enroll'
    // - 携带 booking source（组件入口）→ 'home'
    // - 其他 → 'schedule'
    drawerEntryPage() {
      if (this.mode === 'select') return 'location'
      if (this.mode === 'enroll') return 'enroll'
      const bookingFlow = this.$store.state.bookingFlow || {}
      const fromComponent = bookingFlow.componentId || bookingFlow.componentName || bookingFlow.originPage
      return fromComponent ? 'home' : 'schedule'
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.pendingInit = true
          this.loadFailed = false
          this.mapLoadFailed = false
          this.loadLocations()
          this.trackMapDrawerShow()
          this.$nextTick(() => {
            this.fixDrawerZIndex()
          })
        }
      },
    },
    selectedIndex() {
      this.updateMapFocus()
    },
  },
  mounted() {
    this.enrollLoadingHandler = (payload) => {
      if (!payload) return
      if (payload.loading) {
        this.enrollLoadingVenueId = payload.venueId || true
      } else {
        this.enrollLoadingVenueId = null
        this.enrollLoadingSource = null
      }
    }
    bus.$on('enrollFlowLoading', this.enrollLoadingHandler)

    this.locationPermissionGrantedHandler = (coords) => {
      if (!this.drawerVisible || !coords) return
      this.userLat = coords.lat
      this.userLng = coords.lng
      this.hasGeolocation = true
      this.fetchVenueList({ lat: coords.lat, lng: coords.lng }, true)
    }
    bus.$on('locationPermissionGranted', this.locationPermissionGrantedHandler)

    // 首次进入 /book-trial 且抽屉初始即为 visible 时，Element Drawer 不一定触发 opened。
    // 此时主动初始化地图；后续再次打开仍由 handleOpened 负责 resize 和刷新 markers。
    if (this.drawerVisible) {
      this.$nextTick(() => {
        if (!this.adapter) {
          this.initMap()
        }
      })
    }
  },
  beforeDestroy() {
    this.disposeMap()
    clearTimeout(this.popupCopyTimer)
    clearTimeout(this.scrollTimer)
    if (this.enrollLoadingHandler) {
      bus.$off('enrollFlowLoading', this.enrollLoadingHandler)
      this.enrollLoadingHandler = null
    }
    if (this.locationPermissionGrantedHandler) {
      bus.$off('locationPermissionGranted', this.locationPermissionGrantedHandler)
      this.locationPermissionGrantedHandler = null
    }
  },
  methods: {
    handlePopupPhoneClick() {
      trackContactClick({
        currentStep: this.$store.state.bookingFlow && this.$store.state.bookingFlow.currentStep,
        locationId: this.selectedLocation && this.selectedLocation.venueId,
        locationName: this.selectedLocation && this.selectedLocation.name,
      })
      if (this.isWindows) {
        this.copyToClipboard(this.selectedLocation.phone)
      } else {
        window.location.href = `tel:${this.selectedLocation.phone}`
      }
    },
    copyToClipboard(text) {
      const done = () => {
        this.popupCopyTip = 'Phone number copied'
        clearTimeout(this.popupCopyTimer)
        this.popupCopyTimer = setTimeout(() => { this.popupCopyTip = '' }, 2000)
      }
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(done)
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        done()
      }
    },
    async loadLocations() {
      // 已授权（sessionStorage 命中 或 permissions API 显示 granted）时，
      // 直接用真实经纬度调用一次，避免 "默认坐标 + 真实坐标" 重复请求。
      const cachedLat = sessionStorage.getItem('currentLocationLat')
      const cachedLng = sessionStorage.getItem('currentLocationLng')
      if (cachedLat && cachedLng) {
        this.userLat = parseFloat(cachedLat)
        this.userLng = parseFloat(cachedLng)
        this.hasGeolocation = true
        trackEvent(TRACK_EVENTS.GEOLOCATION_RESULT, {
          status: 1,
          lat_lng: `${this.userLat},${this.userLng}`,
        })
        await this.fetchVenueList({ lat: this.userLat, lng: this.userLng }, true)
        return
      }
      const granted = await this.checkGeolocationGranted()
      if (granted && navigator.geolocation) {
        // 已授权但本会话还没拿到坐标：等系统返回坐标后只调一次接口
        this.requestUserLocation()
        return
      }
      // 未授权 / 状态未知：先用默认坐标兜底渲染，授权流程在后台并行
      await this.fetchVenueList(DEFAULT_COORDS, false)
      this.requestUserLocation()
    },
    checkGeolocationGranted() {
      if (typeof navigator === 'undefined' || !navigator.permissions) {
        return Promise.resolve(false)
      }
      return navigator.permissions.query({ name: 'geolocation' })
        .then((status) => status.state === 'granted')
        .catch(() => false)
    },
    async fetchVenueList(coords, locationAuthStatus) {
      try {
        const res = await this.$axios.post(growthApi.gradeVenueList, {
          clientLat: coords.lat,
          clientLng: coords.lng,
          locationAuthStatus,
        }, { rewritePostBody: true })
        const venueList = res && res.code === 0 && res.data && Array.isArray(res.data.venueList)
          ? res.data.venueList
          : null
        if (venueList && venueList.length > 0) {
          this.loadFailed = false
          this.locations = venueList.map((item) => ({
            venueId: item.id,
            name: item.venueName,
            address: item.address,
            phone: item.phone,
            email: item.email,
            lat: item.lat,
            lng: item.lng,
            photo: item.imageUrl,
            workTime: item.workTime,
            isOpen: item.isOpen,
            businessHoursList: item.businessHoursList || [],
            eventTypeUrl: item.eventTypeUrl || '',
            distance: item.distance,
            distanceUnit: item.distanceUnit || '',
            distanceText: item.distanceText || '',
          }))
          this.syncSelectedIndexFromStore()
          this.$nextTick(() => {
            this.addMarkers()
            this.updateMapFocus()
          })
        } else {
          this.loadFailed = true
        }
      } catch (e) {
        console.warn('[LocationDrawer] fetchVenueList failed:', e)
        this.loadFailed = true
      }
    },

    syncSelectedIndexFromStore() {
      const selected = this.$store.state.bookingFlow && this.$store.state.bookingFlow.selectedLocation
      if (!selected || !selected.venueId) return
      const idx = this.locations.findIndex((loc) => String(loc.venueId) === String(selected.venueId))
      if (idx >= 0) {
        this.selectedIndex = idx
      }
    },
    handleOpened() {
      // el-drawer 过渡结束后再初始化地图，避免容器宽度为 0 导致渲染异常
      if (!this.pendingInit) return
      this.pendingInit = false
      if (this.adapter && this.mapReady) {
        // 复用已有地图实例：仅触发 resize + 刷新 markers，避免重复加载/初始化
        this.adapter.resize()
        this.addMarkers()
        this.updateMapFocus()
      } else {
        this.initMap()
      }
    },
    requestUserLocation() {
      const cachedLat = sessionStorage.getItem('currentLocationLat')
      const cachedLng = sessionStorage.getItem('currentLocationLng')
      if (cachedLat && cachedLng) {
        this.userLat = parseFloat(cachedLat)
        this.userLng = parseFloat(cachedLng)
        this.hasGeolocation = true
        trackEvent(TRACK_EVENTS.GEOLOCATION_RESULT, {
          status: 1,
          lat_lng: `${this.userLat},${this.userLng}`,
        })
        this.fetchVenueList({ lat: this.userLat, lng: this.userLng }, true)
        return
      }
      if (!navigator.geolocation) {
        trackEvent(TRACK_EVENTS.GEOLOCATION_RESULT, { status: 0, lat_lng: '' })
        return
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.userLat = position.coords.latitude
          this.userLng = position.coords.longitude
          this.hasGeolocation = true
          sessionStorage.setItem('currentLocationLat', String(this.userLat))
          sessionStorage.setItem('currentLocationLng', String(this.userLng))
          trackEvent(TRACK_EVENTS.GEOLOCATION_RESULT, {
            status: 1,
            lat_lng: `${this.userLat},${this.userLng}`,
          })
          this.fetchVenueList({ lat: this.userLat, lng: this.userLng }, true)
        },
        (error) => {
          console.warn('[LocationDrawer] Geolocation error:', error.message)
          this.hasGeolocation = false
          trackEvent(TRACK_EVENTS.GEOLOCATION_RESULT, { status: 0, lat_lng: '' })
          if (error.code === 1) {
            this.watchPermissionChange()
          }
        },
        { timeout: 5000, maximumAge: 300000 },
      )
    },
    trackMapDrawerShow() {
      const bookingFlow = this.$store.state.bookingFlow || {}
      trackEvent(TRACK_EVENTS.MAP_DRAWER_SHOW, {
        entry_page: this.drawerEntryPage,
        lego_id: bookingFlow.componentId || '',
      })
    },
    handleCardDeselect() {
      this.selectedIndex = -1
      this.showPopup = false
      if (this.adapter && this.mapReady) {
        this.addMarkers()
      }
    },
    handleCardSelect(index) {
      const loc = this.displayLocations[index]
      if (loc) {
        trackEvent(TRACK_EVENTS.LOCATION_CARD_CLICK, {
          location_id: loc.venueId,
          location_name: loc.name,
          distance: formatLocationDistance(loc.distance, loc.distanceUnit),
          ranking: index + 1,
          has_colsest_tag: index === 0 && this.hasGeolocation,
          type: 'list',
        })
      }
      this.selectLocation(index)
    },
    watchPermissionChange() {
      if (!navigator.permissions) return
      navigator.permissions.query({ name: 'geolocation' }).then((status) => {
        if (status.state === 'granted') {
          this.requestUserLocation()
          return
        }
        status.addEventListener('change', () => {
          if (status.state === 'granted') {
            this.requestUserLocation()
          }
        }, { once: true })
      })
    },
    fixDrawerZIndex() {
      const wrapper = this.$el && this.$el.closest('.el-drawer__wrapper')
      if (wrapper) {
        wrapper.style.zIndex = '3100'
      }
      const modals = document.querySelectorAll('.v-modal')
      for (let i = 0; i < modals.length; i += 1) {
        if (parseInt(modals[i].style.zIndex, 10) >= 3100) {
          modals[i].style.zIndex = '3099'
        }
      }
    },

    async initMap() {
      if (!this.$refs.mapContainer || this.adapter) return

      try {
        this.adapter = new GoogleMapAdapter({
          container: this.$refs.mapContainer,
          center: MAP_DEFAULT_CENTER,
          zoom: MAP_DEFAULT_ZOOM,
          mapId: process.env.googleMapsId,
          apiKey: process.env.googleMapsApiKey,
          language: 'en',
        })
        await this.adapter.init()
        await this.adapter.attachProjectionHelper()
        this.mapReady = true

        // 用户手动拖动 / 缩放地图时关闭 popup（仅响应用户主动操作，
        // 程序触发的 panTo/setZoom 不会触发 dragend / 用户手势 zoom）
        const closePopupOnUserGesture = () => {
          if (this.showPopup) {
            this.showPopup = false
          }
        }
        this.adapter.on('dragstart', closePopupOnUserGesture)
        // popup 已显示时，地图 idle 后刷新浮层像素坐标（如用户缩放后）
        this.adapter.on('idle', () => {
          if (this.showPopup) {
            this.updatePopupPosition()
          }
        })
        // zoom_changed 既包含用户滚轮/双击，也包含 setZoom；用 isUserZoom 标记区分
        this.adapter.on('zoom_changed', () => {
          if (this.programmaticZoom) {
            this.programmaticZoom = false
            return
          }
          closePopupOnUserGesture()
        })

        this.addMarkers()
        this.updateMapFocus()
      } catch (e) {
        console.warn('[LocationDrawer] Google Maps init failed:', e)
        this.mapLoadFailed = true
      }
    },

    addMarkers() {
      if (!this.adapter || !this.mapReady) return

      this.adapter.setMarkers(this.displayLocations, {
        activeId: this.selectedLocation && this.selectedLocation.venueId,
        renderContent: (loc, isActive) => `<div class="location-drawer-pin${isActive ? ' location-drawer-pin--active' : ''}"></div>`,
        onClick: (loc) => {
          const idx = this.displayLocations.findIndex((l) => l.venueId === loc.venueId)
          if (idx >= 0) {
            const target = this.displayLocations[idx]
            trackEvent(TRACK_EVENTS.LOCATION_CARD_CLICK, {
              location_id: target.venueId,
              location_name: target.name,
              distance: formatLocationDistance(target.distance, target.distanceUnit),
              ranking: idx + 1,
              has_colsest_tag: idx === 0 && this.hasGeolocation,
              type: 'popup',
            })
            this.selectLocation(idx)
          }
        },
      })
    },

    updatePopupPosition() {
      if (!this.adapter || !this.selectedLocation) return
      const point = this.adapter.latLngToPixel({
        lat: this.selectedLocation.lat,
        lng: this.selectedLocation.lng,
      })
      if (!point) return
      // AdvancedMarker 默认 bottom-center 锚点：图标底部对齐坐标 (x, y)
      // popup 用 transform: translate(-50%, -100%)，底部对齐 popupPosition.y
      // 卡片底部带向下倒三角（高 8px），三角尖端要正好对齐 marker 顶端
      // popup 底部（含三角尖）= 图标顶部 = y - 41
      this.popupPosition = { x: point.x, y: point.y - 41 }
    },

    updateMapFocus() {
      if (!this.adapter || !this.mapReady || !this.selectedLocation) return
      const loc = this.selectedLocation
      // 切换 marker 时先隐藏旧 popup，避免 flyTo 动画过程中用过渡中的地图状态
      // 计算像素，出现"先在错位置渲染一帧、再瞬移到正确位置"的偏移
      this.showPopup = false
      // 选中卡片时仅平移到该点：若当前缩放小于阈值（看不清门店周边）才拉近，
      // 已经较近时保留用户当前缩放，避免每次选卡片都被强制 zoom in
      const currentZoom = this.adapter.map && this.adapter.map.getZoom
        ? this.adapter.map.getZoom()
        : null
      const MIN_FOCUS_ZOOM = 12
      const flyOptions = {
        // 教学点落在视口从上往下 2/3 处（从下往上 1/3），为上方 popup 留空
        focusAtViewportRatio: 2 / 3,
        onSettled: () => {
          this.updatePopupPosition()
          this.showPopup = true
        },
      }
      if (Number.isFinite(currentZoom) && currentZoom < MIN_FOCUS_ZOOM) {
        this.programmaticZoom = true
        this.adapter.flyTo({ lat: loc.lat, lng: loc.lng }, MIN_FOCUS_ZOOM, flyOptions)
      } else {
        this.adapter.flyTo({ lat: loc.lat, lng: loc.lng }, undefined, flyOptions)
      }
      this.addMarkers()
    },

    handlePopupClose() {
      this.showPopup = false
    },

    handlePopupCenterPageClick(event) {
      this.trackLocationEvaluationClick(this.selectedLocation, 'popup', 'gotocenterpage')
      // 中心页 URL 未配置时（'#' 占位），阻止默认锚点跳转避免页面滚到顶部
      if (this.popupCenterPageUrl === '#') {
        event.preventDefault()
      }
    },

    disposeMap() {
      if (this.adapter) {
        this.adapter.destroy()
        this.adapter = null
      }
      this.mapReady = false
    },

    selectLocation(index) {
      this.selectedIndex = index
      // 等卡片展开/收起动画结束（300ms）后再 scrollIntoView，
      // 否则其他卡片正在收起、列表整体高度还在变化，
      // 浏览器按动画中的位置滚动会导致目标卡片停在中间位置。
      clearTimeout(this.scrollTimer)
      this.scrollTimer = setTimeout(() => {
        const cardEl = this.$refs[`card-${index}`]
        if (cardEl && cardEl[0] && cardEl[0].$el) {
          cardEl[0].$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 320)
    },

    // 按 button_type 上报 location_evaluation_click：
    // 1 预约诊断（Schedule an Assessment）/ 2 use_location / 3 gotocenterpage / 4 enroll_now
    trackLocationEvaluationClick(location, type, scenario) {
      const BUTTON_MAP = {
        schedule: { name: 'Schedule an Assessment', type: 1 },
        use_location: { name: 'Use This Location', type: 2 },
        gotocenterpage: { name: 'Go to Center Page', type: 3 },
        enroll_now: { name: 'Enroll Now', type: 4 },
      }
      const btn = BUTTON_MAP[scenario] || BUTTON_MAP.schedule
      trackEvent(TRACK_EVENTS.LOCATION_EVALUATION_CLICK, {
        location_id: (location && location.venueId) || '',
        location_name: (location && location.name) || '',
        entry_page: this.drawerEntryPage,
        type,
        button_name: btn.name,
        button_type: btn.type,
      })
    },
    handleUseLocation(location, type = 'list') {
      this.trackLocationEvaluationClick(location, type, 'use_location')
      this.$emit('use-location', location)
      this.handleClose()
    },

    handleEnroll(location, type = 'list') {
      if (this.enrollLoadingVenueId) return
      this.enrollLoadingSource = type
      this.trackLocationEvaluationClick(location, type, 'enroll_now')
      this.$emit('enroll', location)
    },

    isEnrollLoadingForVenue(location, source) {
      if (!this.enrollLoadingVenueId) return false
      if (source && this.enrollLoadingSource !== source) return false
      if (this.enrollLoadingVenueId === true) return true
      if (!location || !location.venueId) return false
      return String(this.enrollLoadingVenueId) === String(location.venueId)
    },

    handleGoToCenterPage(location, type = 'list') {
      this.trackLocationEvaluationClick(location, type, 'gotocenterpage')
    },

    handleSchedule(location, type = 'list') {
      this.trackLocationEvaluationClick(location, type, 'schedule')
      this.$emit('schedule', location)
      this.handleClose()
    },

    handleClose() {
      this.drawerVisible = false
      this.showPopup = false
      this.pendingInit = false
      // 不销毁地图实例：保留 adapter 与 SDK，下次打开仅 resize + 刷新 markers，
      // 避免重复加载 SDK / 重新创建 Map 实例。销毁逻辑仅在组件卸载时执行（beforeDestroy）。
    },
  },
}
</script>

<style lang="scss">
.location-drawer {
  .el-drawer__header {
    display: none;
  }

  .el-drawer__body {
    padding: 0;
    overflow: hidden;
  }
}

// AdvancedMarker 默认 bottom-center 锚点，图标底部尖端自然对齐坐标
.location-drawer-pin {
  width: 29px;
  height: 41px;
  background: url("~assets/images/bm/icon-location.webp") center / contain no-repeat;
  cursor: pointer;

  &--active {
    background-image: url("~assets/images/betterme/icon/map-venue-active-icon.webp");
    filter: drop-shadow(0 2px 4px rgba(145, 115, 255, 0.4));
  }
}
</style>

<style lang="scss" scoped>
.location-drawer {
  &__container {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #f7f7f7;
  }

  &__header {
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    flex-shrink: 0;
    z-index: 2;
  }

  &__title {
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 600;
    font-size: 28px;
    color: #44454c;
    margin: 0;
    line-height: 27px;
  }

  &__close {
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      opacity: 0.7;
    }
  }

  &__content {
    display: flex;
    flex: 1;
    overflow: hidden;
    position: relative;
  }

  &__list {
    width: 530px;
    flex-shrink: 0;
    overflow-y: auto;
    padding: 16px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: #f3f3f3;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
    z-index: 1;
  }

  &__error-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    background: #f7f7f7;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 40px;
  }

  &__error-icon {
    width: 140px;
    height: 140px;
    background: url("~assets/images/betterme/icon/failed-icon.webp") center / contain no-repeat;
    margin-bottom: 24px;
  }

  &__error-title {
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 600;
    font-size: 28px;
    line-height: 38px;
    color: #44454c;
    margin: 0 0 16px;
  }

  &__error-desc {
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #7c7c7c;
    margin: 0;
    max-width: 320px;
  }

  &__map {
    flex: 1;
    position: relative;
    overflow: hidden;
  }

  &__map-container {
    width: 100%;
    height: 100%;
  }

  &__popup {
    position: absolute;
    width: 276px;
    padding: 7px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    pointer-events: auto;

    // 底部居中倒三角，尖端朝下指向 marker
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: -8px;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 8px solid #fff;
      filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.08));
    }
  }

  &__popup-image {
    position: relative;
    width: 100%;
    height: 168px;
    overflow: hidden;
    // border-radius: 8px;

    img {
      border-radius: 6px;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__popup-close {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;
    z-index: 2;

    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }
  }

  &__popup-info {
    padding: 10px 0 0;
  }

  &__popup-name {
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 18px;
    color: #44454c;
    margin: 0 0 4px;
    line-height: 27px;
  }

  &__popup-address {
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 12px;
    color: #44454c;
    margin: 0 0 12px;
    line-height: 22px;
  }

  &__popup-cta {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 32px;
    background: var(--txt-color-link);
    border: none;
    border-radius: 6px;
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 16px;
    color: #fff;
    text-decoration: none;
    cursor: pointer;
    line-height: 27px;
    margin-bottom: 10px;
    transition: background 0.2s, color 0.2s, border-color 0.2s;

    &:hover {
      opacity: 0.9;
    }

    &--secondary {
      background: #fff;
      color: var(--txt-color-link);
      border: 1px solid var(--txt-color-link);

      &:hover {
        opacity: 1;
        background: var(--txt-color-link);
        color: #fff;
      }
    }

    &.is-loading,
    &[disabled] {
      cursor: not-allowed;
      opacity: 0.8;

      &:hover {
        opacity: 0.8;
      }
    }
  }

  &__popup-cta--use,
  &__popup-cta--secondary {
    font-size: 12px;
    height: 38px;
    border-radius: 10px;
  }

  &__btn-spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    margin-right: 6px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: location-drawer-spin 0.8s linear infinite;
    vertical-align: middle;
  }

  &__popup-cta-group {
    display: flex;
    gap: 4px;
    width: 100%;
    margin-bottom: 4px;

    .location-drawer__popup-cta {
      flex: 1;
      min-width: 0;
      margin-bottom: 0;
    }
  }

  &__popup-phone {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 18px;
    color: var(--txt-color-link);
    text-decoration: underline;
    line-height: 27px;
    cursor: pointer;
  }

  &__popup-copy-tip {
    display: block;
    font-family: 'Roboto Flex', sans-serif;
    font-size: 12px;
    color: #28aa00;
    margin-top: 4px;
    line-height: 18px;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .phone-icon {
    width: 18px;
    height: 18px;
  }
}

@keyframes location-drawer-spin {
  to { transform: rotate(360deg); }
}
</style>
